/**
 * useSystemLeads — Hooks and utilities for overlaying real system leads
 * onto Meta Ads performance data.
 *
 * System leads come from the `leads` table (ingested via lead-intake webhook).
 * The join key is: leads.lead_profile->>'utm_content' = ad_creatives.meta_ad_id
 *
 * This replaces Meta Pixel lead estimates with actual platform lead counts,
 * giving accurate CPL and fulfillment metrics.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type {
  AdCreative,
  AdCampaign,
  AdSet,
  AdPerformanceSnapshot,
  AdPerformanceGeo,
} from "./useAdPerformance";

// ─── Types ──────────────────────────────────────────────────

interface SystemLeadAttribution {
  meta_ad_id: string;
  state_code: string | null;
  lead_date: string;
  system_leads_count: number;
}

export interface SystemLeadsOverlay {
  adLeads: Map<string, number>;       // "meta_ad_id:date" → count
  adsetLeads: Map<string, number>;    // "meta_adset_id:date" → count
  campaignLeads: Map<string, number>; // "meta_campaign_id:date" → count
  stateLeads: Map<string, number>;    // "STATE_CODE:date" → count
  loaded: boolean;
}

const EMPTY_OVERLAY: SystemLeadsOverlay = {
  adLeads: new Map(),
  adsetLeads: new Map(),
  campaignLeads: new Map(),
  stateLeads: new Map(),
  loaded: false,
};

// ─── Hook: fetch attribution data ──────────────────────────

interface SystemLeadsAttributionResult {
  data: SystemLeadAttribution[];
  available: boolean;
}

export function useSystemLeadsAttribution(dateFrom?: string, dateTo?: string) {
  const query = useQuery({
    queryKey: ["system_leads_attribution", dateFrom, dateTo],
    queryFn: async (): Promise<SystemLeadsAttributionResult> => {
      const { data, error } = await (supabase.rpc as any)("get_system_leads_attribution", {
        p_since: dateFrom,
        p_until: dateTo,
      });
      if (error) {
        console.warn("System leads attribution RPC not available:", error.message);
        return { data: [], available: false };
      }
      return {
        data: (data ?? []) as unknown as SystemLeadAttribution[],
        available: true,
      };
    },
    enabled: !!dateFrom && !!dateTo,
    retry: false, // Don't retry if RPC doesn't exist yet
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  return {
    ...query,
    attributionAvailable: query.data?.available ?? false,
    attributionData: query.data?.data ?? [],
  };
}

// ─── Build overlay maps ─────────────────────────────────────

export function buildSystemLeadsOverlay(
  attribution: SystemLeadAttribution[],
  creatives: AdCreative[],
  campaigns: AdCampaign[],
  adSets: AdSet[]
): SystemLeadsOverlay {
  if (!attribution.length) return EMPTY_OVERLAY;

  // Build hierarchy maps: meta_ad_id → { metaCampaignId, metaAdsetId }
  const campaignIdToMeta = new Map<string, string>();
  for (const c of campaigns) campaignIdToMeta.set(c.id, c.meta_campaign_id);

  const adsetIdToMeta = new Map<string, string>();
  const adsetToCampaignInternal = new Map<string, string>();
  for (const as of adSets) {
    adsetIdToMeta.set(as.id, as.meta_adset_id);
    adsetToCampaignInternal.set(as.id, as.campaign_id);
  }

  const adToHierarchy = new Map<string, { metaCampaignId: string; metaAdsetId: string }>();
  for (const cr of creatives) {
    const metaCampaignId = campaignIdToMeta.get(cr.campaign_id);
    const metaAdsetId = adsetIdToMeta.get(cr.adset_id);
    if (metaCampaignId && metaAdsetId) {
      adToHierarchy.set(cr.meta_ad_id, { metaCampaignId, metaAdsetId });
    }
  }

  // Build aggregation maps
  const adLeads = new Map<string, number>();
  const adsetLeads = new Map<string, number>();
  const campaignLeads = new Map<string, number>();
  const stateLeads = new Map<string, number>();

  for (const row of attribution) {
    // Ad level
    const adKey = `${row.meta_ad_id}:${row.lead_date}`;
    adLeads.set(adKey, (adLeads.get(adKey) ?? 0) + row.system_leads_count);

    // Aggregate up to adset and campaign
    const hierarchy = adToHierarchy.get(row.meta_ad_id);
    if (hierarchy) {
      const asKey = `${hierarchy.metaAdsetId}:${row.lead_date}`;
      adsetLeads.set(asKey, (adsetLeads.get(asKey) ?? 0) + row.system_leads_count);

      const cKey = `${hierarchy.metaCampaignId}:${row.lead_date}`;
      campaignLeads.set(cKey, (campaignLeads.get(cKey) ?? 0) + row.system_leads_count);
    }

    // State level (for geo tab)
    if (row.state_code) {
      const sKey = `${row.state_code}:${row.lead_date}`;
      stateLeads.set(sKey, (stateLeads.get(sKey) ?? 0) + row.system_leads_count);
    }
  }

  return { adLeads, adsetLeads, campaignLeads, stateLeads, loaded: true };
}

// ─── Overlay: patch snapshots with system leads ─────────────

export function overlaySystemLeadsOnSnapshots(
  snapshots: AdPerformanceSnapshot[],
  overlay: SystemLeadsOverlay
): AdPerformanceSnapshot[] {
  if (!overlay.loaded) return snapshots;

  return snapshots.map((snap) => {
    let systemCount: number | undefined;

    if (snap.level === "ad" && snap.meta_ad_id) {
      systemCount = overlay.adLeads.get(`${snap.meta_ad_id}:${snap.date}`);
    } else if (snap.level === "adset" && snap.meta_adset_id) {
      systemCount = overlay.adsetLeads.get(`${snap.meta_adset_id}:${snap.date}`);
    } else if (snap.level === "campaign" && snap.meta_campaign_id) {
      systemCount = overlay.campaignLeads.get(`${snap.meta_campaign_id}:${snap.date}`);
    }

    // If we have system data for this key, use it
    if (systemCount !== undefined) {
      return {
        ...snap,
        leads_count: systemCount,
        cost_per_lead: systemCount > 0 ? snap.spend / systemCount : null,
      };
    }

    // If overlay is loaded but no system leads found for this key:
    // This means zero system leads for this ad/date (genuine zero)
    // Set to 0 so we don't show misleading pixel estimates
    return {
      ...snap,
      leads_count: 0,
      cost_per_lead: null,
    };
  });
}

// ─── Overlay: patch geo data with system leads ──────────────

export function overlaySystemLeadsOnGeo(
  geoData: AdPerformanceGeo[],
  overlay: SystemLeadsOverlay
): AdPerformanceGeo[] {
  if (!overlay.loaded) return geoData;

  return geoData.map((row) => {
    if (!row.state_code) return row;

    const systemCount = overlay.stateLeads.get(`${row.state_code}:${row.date}`);

    if (systemCount !== undefined) {
      return {
        ...row,
        leads_count: systemCount,
        cost_per_lead: systemCount > 0 ? row.spend / systemCount : null,
      };
    }

    return {
      ...row,
      leads_count: 0,
      cost_per_lead: null,
    };
  });
}
